import { useState } from 'react';
import styled from '@emotion/styled';
import PropTypes from 'prop-types';
import H2 from '@base/H2/H2';
import P1 from '@base/P1/P1';
import Rating from '@comp/Rating/Rating';
import Button from '@base/Button/Button';
import Like from '@icon/Like';
import Dislike from '@icon/Dislike';
import Drawer from '@base/Drawer/Drawer';
import Liked from '@icon/Liked';
import Disliked from '@icon/Disliked';
import TextField from '@base/TextField/TextField';
import { segment } from '@actions/analytics';
import {
  GTP_SUBSCRIPTION_FEEDBACK_CARD_SUBMITTED,
  PLUS_HOME_FEEDBACK_SUBMITTED,
  SUBSCRIPTION_FEEDBACK_CARD_SUBMITTED
} from '@constants/segment';
import useGoalEventProps from '@hooks/useGoalEventProps';
import {
  Spacings,
  PRIMARY_BORDER_WIDTH,
  SOLID_BORDER_STYLE,
  mediaQuery
} from '@styles/index';
import useEducatorContext from '@hooks/useEducatorContext';
import { fetchSubscriptionFeedbackCards } from '@api/fetchers/auth';

const StyH2 = styled(H2)`
  margin-top: ${Spacings.SPACING_11B};
  grid-column: span 10;
  display: flex;
  justify-content: center;
  ${mediaQuery.uptoMobile} {
    margin-top: ${Spacings.SPACING_0B};
  }
`;

const StyP1 = styled(P1)`
  margin-top: ${Spacings.SPACING_3B};
  margin-bottom: ${Spacings.SPACING_8B};
  grid-column: span 10;
  text-align: center;
  ${mediaQuery.uptoMobile} {
    margin: ${Spacings.SPACING_1B} ${Spacings.SPACING_0B} ${Spacings.SPACING_6B};
  }
`;
const Sty2P1 = styled(P1)`
  margin-bottom: ${Spacings.SPACING_5B};
  grid-column: span 10;
  justify-content: center;
  display: flex;
  ${mediaQuery.uptoMobile} {
    margin-bottom: ${Spacings.SPACING_4B};
    color: var(--color-text-secondary);
  }
`;

const StyledRating = styled(Rating)`
  align-items: center;
  justify-content: center;
  grid-column: span 10;
  display: flex;
  margin-bottom: ${Spacings.SPACING_12B};
`;

const OptionName = styled(P1)`
  margin-left: ${Spacings.SPACING_3B};
  color: var(--color-text-primary);
  ${mediaQuery.uptoMobile} {
    margin-left: ${Spacings.SPACING_0B};
  }
`;

const LikeButton = styled(Like)`
  border-width: ${PRIMARY_BORDER_WIDTH};
  border-style: ${SOLID_BORDER_STYLE};
  border-color: var(--color-divider);
  padding: ${Spacings.SPACING_2B};
  border-radius: 50%;
  cursor: pointer;
`;

const LikedButton = styled(Liked)`
  border-width: ${PRIMARY_BORDER_WIDTH};
  border-style: ${SOLID_BORDER_STYLE};
  border-color: var(--color-divider);
  padding: ${Spacings.SPACING_2B};
  border-radius: 50%;
  background: ${({ $likedDislikedBgColor }) => $likedDislikedBgColor};
  cursor: pointer;
`;

const DislikeButton = styled(Dislike)`
  margin-left: ${Spacings.SPACING_4B};
  border-width: ${PRIMARY_BORDER_WIDTH};
  border-style: ${SOLID_BORDER_STYLE};
  border-color: var(--color-divider);
  padding: ${Spacings.SPACING_2B};
  border-radius: 50%;
  cursor: pointer;
`;

const DislikedButton = styled(Disliked)`
  margin-left: ${Spacings.SPACING_4B};
  border-width: ${PRIMARY_BORDER_WIDTH};
  border-style: ${SOLID_BORDER_STYLE};
  border-color: var(--color-divider);
  padding: ${Spacings.SPACING_2B};
  border-radius: 50%;
  background: ${({ $likedDislikedBgColor }) => $likedDislikedBgColor};
  cursor: pointer;
`;

const Option = styled.div`
  grid-column: span 10;
  width: 423px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: ${Spacings.SPACING_4B};
  padding-bottom: ${Spacings.SPACING_3B};
  border-bottom: ${({ $showBorder }) =>
    $showBorder &&
    `${PRIMARY_BORDER_WIDTH} ${SOLID_BORDER_STYLE} var(--color-divider)`};
  ${mediaQuery.uptoMobile} {
    width: 100%;
    height: 56px;
    margin-bottom: ${Spacings.SPACING_0B};
    padding: ${Spacings.SPACING_0B};
  }
`;

const Flex = styled.div`
  display: flex;
  margin-right: ${Spacings.SPACING_5B};
  ${mediaQuery.uptoMobile} {
    margin-right: ${Spacings.SPACING_0B};
  }
`;

const Input = styled(TextField)`
  grid-column: span 10;
  margin-top: ${Spacings.SPACING_3B};
  margin-bottom: ${Spacings.SPACING_10B};
  color: var(--color-text-primary);
  ${mediaQuery.uptoMobile} {
    margin: ${Spacings.SPACING_12B} 0 ${Spacings.SPACING_4B};
    & input {
      min-height: 96px;
    }
  }
`;

const Submit = styled(Button)`
  align-self: flex-start;
  ${mediaQuery.uptoMobile} {
    width: 100%;
    grid-column: span 10;
    margin-bottom: ${Spacings.SPACING_8B};
  }
`;

const OptionCard = ({
  option,
  showBorder,
  select,
  deselect,
  isUpvoted,
  isDownvoted,
  likedDislikedBgColor
}) => {
  return (
    <Option $showBorder={showBorder}>
      <OptionName>{option.name}</OptionName>
      <Flex>
        {isUpvoted ? (
          <LikedButton
            size={Spacings.SPACING_8B}
            onClick={deselect(option)}
            color="var(--color-base-1)"
            $likedDislikedBgColor={likedDislikedBgColor}
          />
        ) : (
          <LikeButton
            size={Spacings.SPACING_8B}
            onClick={select(option, 1)}
            color="var(--color-text-primary)"
          />
        )}
        {isDownvoted ? (
          <DislikedButton
            size={Spacings.SPACING_8B}
            color="var(--color-base-1)"
            onClick={deselect(option)}
            $likedDislikedBgColor={likedDislikedBgColor}
          />
        ) : (
          <DislikeButton
            size={Spacings.SPACING_8B}
            onClick={select(option, -1)}
            color="var(--color-text-primary)"
          />
        )}
      </Flex>
    </Option>
  );
};

const RateYourExperienceDetails = ({
  ratingValue,
  setRatingValue,
  feedBackDetails,
  submitFeedback,
  getFeebackOfAObject,
  objectUID,
  onClose,
  drawerState,
  source,
  goalUID,
  entityTitle,
  entityContentType,
  lastPrimarySource
}) => {
  const isEducatorSide = useEducatorContext();
  const [selectedOptions, setSelectedOption] = useState([]);
  const [suggestion, setSuggestion] = useState('');
  const likedDislikedBgColor = 'var(--color-base-fill)';
  const goalSegmentData = useGoalEventProps({ goalUID });
  const title = feedBackDetails?.description;
  const handleClick = (value) => () => setRatingValue(value);
  const selectedChoice = feedBackDetails?.choices?.filter(
    (choice) => choice.description === `${ratingValue}`
  )[0];

  const { options, id: selectedChoiceUID } = selectedChoice || {};
  const [isSubmittingFeedback, setIsSubmittingFeedback] = useState(false);

  const isUpvoted = (choice) =>
    !!selectedOptions.find(
      (option) => option.id === choice.id && option.value === 1
    );
  const isDownvoted = (choice) =>
    !!selectedOptions.find(
      (option) => option.id === choice.id && option.value === -1
    );
  const deselect = (option) => () =>
    setSelectedOption(
      selectedOptions.filter((innerOption) => innerOption.id !== option.id)
    );
  const select = (option, value) => () => {
    setSelectedOption([
      ...selectedOptions.filter((innerOption) => innerOption.id !== option.id),
      { ...option, value }
    ]);
  };

  const sanitizeFeedbackData = (option) => {
    return `review_${option.name
      .replace(/[^a-zA-Z ]/g, '')
      .replace(/ +(?= )/g, '')
      .split(' ')
      .map((word) => word.toLowerCase())
      .join('_')}`;
  };

  const getSegmentDataForPlusHomeFeedback = (selectedFeedBack = []) => {
    const segData = {};
    options.forEach((option) => {
      const name = sanitizeFeedbackData(option);
      segData[name] = null;
    });
    selectedFeedBack.forEach((option) => {
      const name = sanitizeFeedbackData(option);
      segData[name] = option.value;
    });
    segData.plus_experience_rating = ratingValue;
    return segData;
  };

  const handleSubmit = () => {
    setIsSubmittingFeedback(true);
    submitFeedback(
      {
        others: suggestion,
        options: selectedOptions,
        choice_id: selectedChoiceUID,
        object_id: objectUID
      },
      feedBackDetails?.uid
    ).then((response) => {
      setIsSubmittingFeedback(false);
      if (
        source === PLUS_HOME_FEEDBACK_SUBMITTED ||
        source === SUBSCRIPTION_FEEDBACK_CARD_SUBMITTED
      ) {
        const segmentData = getSegmentDataForPlusHomeFeedback(selectedOptions);
        let updateSegmentData = { ...segmentData, ...goalSegmentData };
        if (source === SUBSCRIPTION_FEEDBACK_CARD_SUBMITTED) {
          updateSegmentData = {
            ...updateSegmentData,
            entity_uid: objectUID,
            entity_title: entityTitle,
            entity_content_type: entityContentType,
            last_primary_source: lastPrimarySource
          };
        }
        segment.track(source, {
          ...updateSegmentData
        });
      }
      if (source === GTP_SUBSCRIPTION_FEEDBACK_CARD_SUBMITTED) {
        fetchSubscriptionFeedbackCards(goalUID);
      }
      if (response.result) {
        if (getFeebackOfAObject) getFeebackOfAObject(objectUID);
        onClose({ hasSubmittedFeedback: true });
        setSuggestion('');
        setSelectedOption([]);
      }
    });
  };

  return (
    <Drawer drawerState={drawerState} onClose={onClose}>
      <StyH2 color="var(--color-text-primary)">Rate your experience</StyH2>
      <StyP1 color="var(--color-text-primary)">
        {title || 'How is your learning experience?'}
      </StyP1>
      <StyledRating
        value={ratingValue}
        setRating={handleClick}
        starColor="var(--color-i-orange-1)"
      />
      <Sty2P1 color="var(--color-text-primary)">
        How satisfied are you with
      </Sty2P1>
      {options?.map((option, index) => (
        <OptionCard
          option={option}
          showBorder={index + 1 !== options.length}
          key={option.id}
          select={select}
          deselect={deselect}
          isUpvoted={isUpvoted(option)}
          isDownvoted={isDownvoted(option)}
          likedDislikedBgColor={likedDislikedBgColor}
        />
      ))}
      <Input
        placeholder="Tell us more about your experience"
        value={suggestion}
        onChange={(e) => setSuggestion(e.target.value)}
      />
      <Submit
        label="Submit"
        onClick={handleSubmit}
        variant={isSubmittingFeedback ? 'disabled' : 'filled'}
        color={isEducatorSide ? 'blue' : 'green'}
      />
    </Drawer>
  );
};

export default RateYourExperienceDetails;

RateYourExperienceDetails.propTypes = {
  ratingValue: PropTypes.number.isRequired,
  setRatingValue: PropTypes.func.isRequired,
  feedBackDetails: PropTypes.objectOf(PropTypes.any).isRequired,
  submitFeedback: PropTypes.func.isRequired,
  getFeebackOfAObject: PropTypes.func,
  objectUID: PropTypes.string.isRequired,
  onClose: PropTypes.func.isRequired,
  drawerState: PropTypes.bool.isRequired,
  source: PropTypes.string,
  goalUID: PropTypes.string,
  entityTitle: PropTypes.string,
  entityContentType: PropTypes.string,
  lastPrimarySource: PropTypes.string
};

RateYourExperienceDetails.defaultProps = {
  getFeebackOfAObject: null,
  source: null,
  goalUID: null,
  entityTitle: '',
  entityContentType: '',
  lastPrimarySource: ''
};

OptionCard.propTypes = {
  option: PropTypes.objectOf(PropTypes.any).isRequired,
  showBorder: PropTypes.bool.isRequired,
  select: PropTypes.func.isRequired,
  deselect: PropTypes.func.isRequired,
  isUpvoted: PropTypes.bool.isRequired,
  isDownvoted: PropTypes.bool.isRequired,
  likedDislikedBgColor: PropTypes.string
};

OptionCard.defaultProps = {
  likedDislikedBgColor: 'var(--color-base-fill)'
};
